<script setup lang="ts">
import { ref } from 'vue'

import PasswordField from './PasswordField.vue'
import PasswordValidation from './PasswordValidation.vue'

export type PasswordFormContext = 'renew' | 'register'

const props = withDefaults(defineProps<{
  password?: string,
  cssFormItemClass?: string,
  formContext?: PasswordFormContext
}>(), {
  password: '',
  formContext: 'renew'
})

const emit = defineEmits<{
  (e: 'passwordUpdate', value: string): void
}>()

const contextBasedWording: Record<PasswordFormContext, { label: Record<string, string>, placeholder: Record<string, string> }> = {
  register: {
    label: {
      select_password: 'Passwort wählen',
      repeat_password: 'Passwort wiederholen'
    },
    placeholder: {
      select_password: 'Dein gewünschtes Passwort',
      repeat_password: 'Passwort wiederholen'
    }
  },
  renew: {
    label: {
      select_password: 'Neues Passwort wählen',
      repeat_password: 'Neues Passwort wiederholen'
    },
    placeholder: {
      select_password: 'Dein neues Passwort',
      repeat_password: 'Neues Passwort wiederholen'
    }
  }
}

const passwordAccurate = ref(false)
const passwordError = ref<string | null>(null)
const passwordRepeat = ref('')
const passwordRepeatError = ref<string | null>(null)

const validate = () => {
  if (props.password.length === 0) {
    passwordError.value = 'Bitte trage dein gewünschtes Passwort ein.'
  } else if (!passwordAccurate.value) {
    passwordError.value = 'Dein Passwort erfüllt nicht alle notwendigen Sicherheitskriterien.'
  } else {
    passwordError.value = null
  }

  if (passwordRepeat.value.length === 0) {
    passwordRepeatError.value = 'Bitte wiederhole dein gewünschtes Passwort.'
  } else if (props.password !== passwordRepeat.value) {
    passwordRepeatError.value = 'Dein gewünschtes Passwort und die Wiederholung stimmen nicht überein.'
  } else {
    passwordRepeatError.value = null
  }

  return !passwordError.value && !passwordRepeatError.value
}

defineExpose({ validate })
</script>

<template>
  <div>
    <div :class="['bx-form__item', cssFormItemClass, {'bx-has-error': passwordError}]">
      <label for="select_password"
             class="bx-form__label">
        {{ contextBasedWording[formContext].label.select_password }}
      </label>
      <PasswordField id="select_password"
                     :password="password"
                     :placeholder="contextBasedWording[formContext].placeholder.select_password"
                     class="bx-form__validation-follows-input"
                     autocomplete="new-password"
                     @password-update="emit('passwordUpdate', $event)"
                     @focusout="passwordError = null" />
      <span v-if="passwordError"
            class="bx-form__message bx-form__message--error">
        {{ passwordError }}
      </span>
    </div>
    <PasswordValidation :password="password"
                        @valid="passwordAccurate = $event" />
    <div :class="['bx-form__item', cssFormItemClass, {'bx-has-error': passwordRepeatError}]">
      <label for="repeat_password"
             class="bx-form__label">
        {{ contextBasedWording[formContext].label.repeat_password }}
      </label>
      <PasswordField id="repeat_password"
                     :password="passwordRepeat"
                     :placeholder="contextBasedWording[formContext].placeholder.repeat_password"
                     autocomplete="new-password"
                     @password-update="passwordRepeat = $event"
                     @focusout="passwordRepeatError = null;" />
      <span v-if="passwordRepeatError"
            class="bx-form__message bx-form__message--error">
        {{ passwordRepeatError }}
      </span>
    </div>
  </div>
</template>
